// In-place language switch: fetches the same page in the other language and swaps
// the document body, direction and title without a full navigation, keeping the
// scroll position. Falls back to the normal link if anything fails.
class LanguageSwitch extends HTMLElement {
  #controller = null;
  #busy = false;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #onClick(event) {
    const link = event.target.closest('a[href]');
    if (!link || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.getAttribute('aria-current') === 'true') {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.#swap(link.href);
  }

  async #swap(url) {
    if (this.#busy) return;
    this.#busy = true;
    const scrollY = window.scrollY;
    try {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const next = new DOMParser().parseFromString(html, 'text/html');
      if (!next.body) throw new Error('No body');

      // Load any stylesheets the other language needs (e.g. the Arabic fonts) before swapping.
      const pending = [];
      next.head.querySelectorAll('link[rel="stylesheet"], link[rel="preconnect"]').forEach((node) => {
        const href = node.getAttribute('href');
        if (!href || document.head.querySelector(`link[href="${CSS.escape(href)}"]`)) return;
        const clone = document.importNode(node, true);
        if (clone.rel === 'stylesheet') {
          pending.push(new Promise((resolve) => {
            clone.addEventListener('load', resolve, { once: true });
            clone.addEventListener('error', resolve, { once: true });
            setTimeout(resolve, 1500);
          }));
        }
        document.head.append(clone);
      });
      await Promise.all(pending);

      const apply = () => {
        const root = document.documentElement;
        const nextRoot = next.documentElement;
        root.lang = nextRoot.lang || root.lang;
        if (nextRoot.dir) root.dir = nextRoot.dir;
        else root.removeAttribute('dir');
        document.title = next.title;
        document.body.replaceWith(document.importNode(next.body, true));
        window.scrollTo(0, scrollY);
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (document.startViewTransition && !reduceMotion) {
        await document.startViewTransition(apply).finished.catch(() => {});
      } else {
        apply();
      }
      history.replaceState(history.state, '', url);
    } catch (error) {
      window.location.href = url;
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('s-language-switch')) customElements.define('s-language-switch', LanguageSwitch);
